import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { postMapGame } from "../../utils/mapGameApi";
import { completedSetFromProgress, isCompletedBySet } from "../../utils/mapGameProgressCheck";
import "./categories.css";

const S3 = (process.env.REACT_APP_S3_PATH || "").trim();

function toAssetUrl(filename) {
  if (!filename) return "";
  if (/^https?:\/\//i.test(filename)) return filename;
  const rel = String(filename).replace(/^\/+/, "");
  return `${S3}${encodeURIComponent(rel).replace(/%2F/g, "/")}`;
}

export default function Categories() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: themeData, status } = useSelector((s) => s.theme);
  const [activeIdx, setActiveIdx] = useState(0);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);
  const [completedSet, setCompletedSet] = useState(() => new Set());

  const categories = useMemo(
    () => (Array.isArray(themeData?.categoriesList) ? themeData.categoriesList : []),
    [themeData]
  );

  const canGoPrev = activeIdx > 0;
  const canGoNext = activeIdx < categories.length - 1;
  const selected = categories[activeIdx] || null;
  const isCategoryCompleted = (cat) => isCompletedBySet(cat, completedSet);

  const fetchCompletedSetFromServer = async () => {
    if (!user || !adminToken) return completedSet;
    const uid = user.userId || user.id;
    const prog = await postMapGame(adminToken, "/mapGameProgress", {
      userId: uid,
      email: user.email,
      name: user.name,
    });
    const nextDone = completedSetFromProgress(prog);
    setCompletedSet(nextDone);
    return nextDone;
  };

  useEffect(() => {
    const nextBack = user?.backButtonRedirect || "/login?&save=true";
    dispatch(setBackButtonUrl(nextBack));
  }, [dispatch, user?.backButtonRedirect]);

  useEffect(() => {
    if (status !== "succeeded" || !themeData || !user || !adminToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const uid = user.userId || user.id;
        await postMapGame(adminToken, "/mapGameInit", {
          userId: uid,
          email: user.email,
          name: user.name,
        });
        const prog = await postMapGame(adminToken, "/mapGameProgress", {
          userId: uid,
          email: user.email,
          name: user.name,
        });
        if (cancelled) return;
        setCompletedSet(completedSetFromProgress(prog));
      } catch (e) {
        console.error("mapGame init/progress:", e);
        if (!cancelled) setCompletedSet(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, themeData, user, adminToken]);

  useEffect(() => {
    if (!categories.length) return undefined;
    const firstIncomplete = categories.findIndex((cat) => !isCategoryCompleted(cat));
    if (firstIncomplete >= 0) {
      setActiveIdx(firstIncomplete);
      return undefined;
    }
    navigate("/leaderboard", { replace: true });
    return undefined;
  }, [categories, completedSet, navigate]);

  if (status !== "succeeded" || !themeData) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  const bg = themeData?.button_backgroundcolor || "#8b5a2b";
  const fg = themeData?.button_color || "#fff";
  const selectedCompleted = selected ? isCategoryCompleted(selected) : false;

  return (
    <div className="cat-page">
      <h1 className="cat-title" style={{ color: themeData?.text_color || "#4f3216" }}>
        CATEGORIES
      </h1>
      <div className="cat-slider-wrap">
        <button
          type="button"
          className="cat-nav-btn"
          onClick={() => canGoPrev && setActiveIdx((p) => p - 1)}
          disabled={!canGoPrev}
          aria-label="Previous category"
        >
          &#10094;
        </button>
        <div className="cat-track">
          {categories.map((cat, idx) => {
            const isActive = idx === activeIdx;
            const isDone = isCategoryCompleted(cat);
            return (
              <button
                key={cat.id}
                type="button"
                className={`cat-card${isActive ? " is-active" : ""}${isDone ? " is-done" : ""}`}
                onClick={() => setActiveIdx(idx)}
              >
                {cat.image ? (
                  <img src={toAssetUrl(cat.image)} alt={cat.name} className="cat-card-img" />
                ) : (
                  <div className="cat-card-img-placeholder">
                    <i className="fa-solid fa-image" />
                  </div>
                )}
                <div className="cat-card-name">{cat.name}</div>
                {isDone ? <div className="cat-card-done-pill">Already Played</div> : null}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="cat-nav-btn"
          onClick={() => canGoNext && setActiveIdx((p) => p + 1)}
          disabled={!canGoNext}
          aria-label="Next category"
        >
          &#10095;
        </button>
      </div>
      <button
        type="button"
        className={`cat-next-btn${selectedCompleted ? " is-disabled" : ""}`}
        style={{ color: fg, backgroundColor: bg, border: `2px solid ${fg}` }}
       
        disabled={!selected || selectedCompleted}
        onClick={() => {
          if (!selected) return;
          if (selectedCompleted) return;
          if (!user || !adminToken) {
            navigate(
              `/game-map?category=${encodeURIComponent(
                selected.id || selected._id || selected.categoryId || ""
              )}`
            );
            return;
          }
          fetchCompletedSetFromServer()
            .then((doneSet) => {
              const alreadyPlayed = isCompletedBySet(selected, doneSet);
              if (alreadyPlayed) return;
              navigate(
                `/game-map?category=${encodeURIComponent(
                  selected.id || selected._id || selected.categoryId || ""
                )}`
              );
            })
            .catch((e) => {
              console.error("mapGameProgress on next:", e);
              Swal.fire("Error", "Could not verify category progress. Please try again.", "error");
            });
        }}
      >
        {selectedCompleted ? "Already Played" : "Next"}
      </button>
    </div>
  );
}
